import {KEY_POINT_TOKENS} from '../constants/tokens';

/**
 * БЛИКИ НА ЖЕТОНАХ КЛЮЧЕВЫХ ТОЧЕК. Время от времени по жетону пробегает свет:
 *
 *  - по МЕТАЛЛИЧЕСКИМ жетонам (великие руины, великий рудник) — резкий блеск,
 *    как по монете, на которую упал луч: яркая узкая полоса и тонкая вслед;
 *  - по КАМЕННЫМ (обычные руины) — мягкий световой блик: широкое теплое пятно
 *    света неспешно проходит по камню. Камень не сверкает — он освещается.
 *
 * У каждого жетона свой счет времени, поэтому светятся они вразнобой, а не все
 * разом. Блики — украшение: в состояние партии не попадают и у каждого клиента
 * свои (см. scenes/TokenGlints).
 *
 * КАК ЧАСТО — задает averageIntervalMs своего материала (ниже): это средний
 * промежуток между бликами ОДНОГО жетона.
 */

/** Общее для всех материалов */
export const TOKEN_GLINT = Object.freeze({
  // Блик обрезан по контуру жетона — шестиугольнику чуть меньше картинки:
  // на эту долю размера, чтобы свет не лег на прозрачный край
  maskInsetShare: 0.02,
  // Насколько слой блика выше слоя жетона: над самим жетоном, но под всем, что
  // лежит выше него (спецстроения, граница владений, горы, отряды)
  depthOffset: 0.002,
  // Самый длинный шаг времени за кадр, мс. Время бликов идет по кадрам: в
  // скрытой вкладке оно стоит, а после возврата не прыгает — иначе все жетоны
  // блеснули бы разом
  maxStepMs: 100,
});

/**
 * Вид блика у каждого материала жетона.
 *
 * texture — картинка блика: рисуется в память один раз на сцену, отдельного
 * файла нет. Это полоса с мягкими краями; stops — яркость по ее ширине
 * (доля ширины, яркость). Блик бежит слева направо, так что правый край
 * картинки идет первым, а левый — последним
 */
export const TOKEN_GLINT_STYLES = Object.freeze({
  // МЕТАЛЛ — блеск
  metal: Object.freeze({
    // КАК ЧАСТО блестит ОДИН жетон: средний промежуток между его проблесками,
    // мс. Главная ручка частоты: 60000 — примерно раз в минуту, 30000 — вдвое
    // чаще. Жетонов на поле много, так что где-нибудь на поле блик виден
    // в разы чаще, чем на одном жетоне
    averageIntervalMs: 60000,
    // Разброс промежутка — доля среднего в обе стороны: 0.5 дает от половины
    // до полутора средних (при минуте — от 30 до 90 секунд). 0 — строго по часам
    intervalSpread: 0.5,
    // Время всего пробега полосы, мс: от момента, когда она еще целиком за
    // левым краем жетона, до ухода за правый. На самом жетоне свет виден
    // примерно половину этого времени (у камня — около двух третей)
    durationMs: 900,
    // Наклон полосы блика, градусы: полоса идет как «/»
    angleDeg: 20,
    // Ширина картинки блика в долях ширины жетона
    bandWidthShare: 0.8,
    // Яркость блика в самом светлом месте полосы
    alpha: 0.85,
    // Цвет света
    tint: 0xFFFFFF,
    // Широкая полоса света (идет первой — она правее) и тонкая вслед за ней
    texture: Object.freeze({
      key: 'token_glint_metal',
      width: 128,
      height: 4,
      stops: Object.freeze([
        [0, 0],
        [0.12, 0],
        [0.18, 0.55],
        [0.24, 0],
        [0.32, 0],
        [0.44, 0.3],
        [0.58, 1],
        [0.72, 0.3],
        [0.86, 0],
        [1, 0],
      ]),
    }),
  }),
  // КАМЕНЬ — световой блик: шире, слабее, медленнее и теплее, чем блеск металла
  stone: Object.freeze({
    // КАК ЧАСТО свет проходит по ОДНОМУ жетону — так же, как у металла (см. выше)
    averageIntervalMs: 60000,
    intervalSpread: 0.5,
    durationMs: 2200,
    angleDeg: 20,
    bandWidthShare: 1.6,
    alpha: 0.42,
    // Теплый солнечный свет
    tint: 0xFFF2D2,
    // Одно широкое пятно, плавно сходящее на нет в обе стороны
    texture: Object.freeze({
      key: 'token_glint_stone',
      width: 128,
      height: 4,
      stops: Object.freeze([
        [0, 0],
        [0.15, 0.04],
        [0.3, 0.3],
        [0.42, 0.78],
        [0.5, 1],
        [0.58, 0.78],
        [0.7, 0.3],
        [0.85, 0.04],
        [1, 0],
      ]),
    }),
  }),
});

/** Какой жетон из какого материала. Жетоны без строки не светятся */
const TOKEN_GLINT_STYLE_BY_TOKEN = Object.freeze({
  [KEY_POINT_TOKENS.SUPER_RUINS]: TOKEN_GLINT_STYLES.metal,
  [KEY_POINT_TOKENS.SUPER_MINE]: TOKEN_GLINT_STYLES.metal,
  [KEY_POINT_TOKENS.RUINS]: TOKEN_GLINT_STYLES.stone,
});

/** Жетоны, по которым бегают блики */
export const TOKEN_GLINT_TOKENS = Object.freeze(Object.keys(TOKEN_GLINT_STYLE_BY_TOKEN));

/**
 * Вид блика для жетона
 * @param {string} tokenType
 * @returns {Object|null} один из TOKEN_GLINT_STYLES; null — жетон не светится
 */
export const getTokenGlintStyle = (tokenType) => (
  Object.prototype.hasOwnProperty.call(TOKEN_GLINT_STYLE_BY_TOKEN, tokenType)
    ? TOKEN_GLINT_STYLE_BY_TOKEN[tokenType]
    : null
);
